import { computed, onMounted, ref, toRaw, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { onClickOutside, useEventListener } from '@vueuse/core'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import {
  useFetchBetslip,
  useTicketsStore,
  type ClientTicket,
} from '@nsftx/seven-client-retail-vue-sgp'
import errorParser from '../../utils/errorParser'
import { checkAndShowSlipDetails } from '@/modules/slip-check/slipCheckService'
import { useSlipRebet } from '@/composables/useSlipRebet/useSlipRebet'
import { printSlipCopy } from '@/modules/slip-copy/slipCopyService'

export interface LatestBetslipAction {
  id: string
  name: string
  icon: string
  onClick: () => Promise<void>
}

interface BetslipActionsPosition {
  left: number
  top?: number
  bottom?: number
}

const LOG_PREFIX = '[useLatestBetslipActions]'
const BETSLIP_TABLE_ACTIONS_ACTIVE_OFFSET_PX = 20
const BETSLIP_TABLE_ACTIONS_DEFAULT_OFFSET_PX: BetslipActionsPosition = { left: 170, top: 35 }
const BETSLIP_ACTIONS_MENU_WIDTH_PX = 170
const BETSLIP_ACTIONS_MENU_HEIGHT_PX = 100
const BETSLIP_TABLE_HEADER_HEIGHT_PX = 32

export const useLatestBetslipActions = () => {
  const activeBetslipId = ref<string | null>(null)
  const betslipActionsPosition = ref(BETSLIP_TABLE_ACTIONS_DEFAULT_OFFSET_PX)
  const latestBetslipsTable = useTemplateRef<HTMLDivElement>('latestBetslipsTable')
  const { tickets: betslips } = storeToRefs(useTicketsStore())

  const selectedBetslip = computed<ClientTicket | undefined>(() =>
    betslips.value.find((betslip) => betslip.feBetslipId === activeBetslipId.value),
  )
  const betslipActions = computed(() =>
    selectedBetslip.value ? getBetslipActions(selectedBetslip.value) : [],
  )

  const { rebetSlip } = useSlipRebet()
  const { betslip, fetchBetslip, errorDetails, error } = useFetchBetslip()
  const { t } = useI18n({ useScope: 'global' })
  const { showNotification } = useNotificationsStore()

  const getBetslipActions = (ticket: ClientTicket) => {
    const { actionType, feBetslipId, linkCode, barcode, localStatus, bets } = ticket
    const betslipActions: LatestBetslipAction[] = []

    switch (actionType) {
      case 'BETSLIP_PAYIN': {
        if (bets?.length) {
          const rebetSlipOptions = {
            linkCode,
            feBetslipId,
            betIds: bets.map(({ id }) => id),
          }
          betslipActions.push({
            id: 'rebet',
            name: t('slip_action_rebet'),
            icon: 'betslip-b',
            onClick: () => rebetSlip(rebetSlipOptions),
          })
        }

        if (barcode) {
          betslipActions.push({
            id: 'check',
            name: t('slip_action_check'),
            icon: 'barcode',
            onClick: () => checkAndShowSlipDetails(barcode),
          })
        }

        if (localStatus === 'ACCEPTED') {
          betslipActions.push({
            id: 'print_copy',
            name: t('slip_action_print_copy'),
            icon: 'print-ng',
            onClick: () => performPrintCopy(feBetslipId, linkCode),
          })
        }

        return betslipActions
      }
      default:
        return betslipActions
    }
  }

  const performPrintCopy = async (feBetslipId: string, linkCode: string) => {
    await fetchBetslip(feBetslipId)
    if (!betslip.value) {
      logService.error(`${LOG_PREFIX} Failed to fetch betslip on print copy action.`, {
        code: 'SS_PRINT_COPY_FETCH_BETSLIP_ERROR',
        ...errorParser.parseUpstream(error.value),
        feBetslipId,
        product_displayid: linkCode,
        details: {
          ...toRaw(errorDetails.value),
        },
      })
      showNotification({
        variant: 'error',
        text: [t('slip_print_copy_failed')],
      })
      return
    }

    printSlipCopy(betslip.value)
  }

  const setBetslipActionsPosition = (tableContainer: HTMLDivElement, event: MouseEvent) => {
    const { left, top, width, height } = tableContainer.getBoundingClientRect()
    const nextLeft = event.clientX - (left - BETSLIP_TABLE_ACTIONS_ACTIVE_OFFSET_PX)
    const nextTop = event.clientY - (top + BETSLIP_TABLE_ACTIONS_ACTIVE_OFFSET_PX)

    const rowElement = (event.target as HTMLElement)?.closest('tr')
    if (!rowElement) {
      return
    }

    const {
      left: rowLeft,
      right: rowRight,
      top: rowTop,
      bottom: rowBottom,
    } = rowElement.getBoundingClientRect()
    const minLeft = Math.max(0, rowLeft - left)
    const maxLeft = Math.max(
      minLeft,
      Math.min(
        width - BETSLIP_ACTIONS_MENU_WIDTH_PX,
        rowRight - left - BETSLIP_ACTIONS_MENU_WIDTH_PX,
      ),
    )
    const rowBottomOffset = rowBottom - top
    const minTop = rowTop - top
    const maxTop = Math.max(minTop, Math.min(height, rowBottomOffset))

    const leftOffset = Math.min(Math.max(nextLeft, minLeft), maxLeft)
    const topOffset = Math.min(Math.max(nextTop, minTop), maxTop)
    const overflowsBottom = topOffset + BETSLIP_ACTIONS_MENU_HEIGHT_PX > height
    const overflowsTop =
      rowBottomOffset - BETSLIP_ACTIONS_MENU_HEIGHT_PX < BETSLIP_TABLE_HEADER_HEIGHT_PX

    betslipActionsPosition.value =
      overflowsBottom && !overflowsTop
        ? {
            left: leftOffset,
            bottom: Math.max(0, height - rowBottomOffset),
          }
        : {
            left: leftOffset,
            top: topOffset,
          }
  }

  const showBetslipActions = (
    tableContainer: HTMLDivElement,
    feBetslipId: string,
    event: Event,
  ) => {
    activeBetslipId.value = feBetslipId

    if (event instanceof MouseEvent) {
      setBetslipActionsPosition(tableContainer, event)
    }
  }

  const hideBetslipActions = () => {
    activeBetslipId.value = null
  }

  onMounted(() => {
    onClickOutside(latestBetslipsTable, hideBetslipActions)
    useEventListener(window, 'blur', () => {
      if (document.activeElement instanceof HTMLIFrameElement) {
        hideBetslipActions()
      }
    })
  })

  return {
    latestBetslipsTable,
    betslipActions,
    betslipActionsPosition,
    showBetslipActions,
    hideBetslipActions,
  }
}
